/**
 * Tanker Dashboard — Tanker status cards with stacked offload bars + receiver fuel status table
 */

import type { AARTanker, AARReceiver } from '../../../../types'
import { fuelPct, fuelColor, offloadPct } from '../../../../services/aarEngine'

interface TankerDashboardProps {
  tankers: AARTanker[]
  receivers: AARReceiver[]
  currentTime: number
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    AVAILABLE: 'bg-emerald-500/20 text-emerald-400',
    REFUELING: 'bg-blue-500/20 text-blue-400',
    RTB: 'bg-amber-500/20 text-amber-400',
    CANCELLED: 'bg-red-500/20 text-red-400',
    WAITING: 'bg-gray-500/20 text-gray-400',
    IN_QUEUE: 'bg-blue-500/20 text-blue-400',
    ON_BOOM: 'bg-cyan-500/20 text-cyan-400',
    COMPLETE: 'bg-emerald-500/20 text-emerald-400',
    DIVERTED: 'bg-amber-500/20 text-amber-400',
    EMERGENCY: 'bg-red-500/20 text-red-400',
  }
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${colors[status] ?? 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  )
}

function FuelBar({ pct, height = 'h-1.5' }: { pct: number; height?: string }) {
  const color = fuelColor(pct)
  return (
    <div className={`w-full ${height} bg-term-bgDark rounded-full overflow-hidden`}>
      <div className={`${height} rounded-full transition-all duration-300`} style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
    </div>
  )
}

function computeOffloadBreakdown(tnk: AARTanker, receivers: AARReceiver[]) {
  const delivered = tnk.offloaded
  // Scheduled = sum of requestedOffload for queued + remaining for current receiver
  let scheduled = 0
  for (const rcvId of tnk.queuedReceivers) {
    const rcv = receivers.find(r => r.id === rcvId)
    if (rcv) scheduled += rcv.requestedOffload
  }
  if (tnk.currentReceiver) {
    const currentRcv = receivers.find(r => r.id === tnk.currentReceiver)
    if (currentRcv) {
      // Remaining for current = requestedOffload minus what they've likely received
      // Approximate: use fuel needed to fill
      const remaining = Math.max(0, currentRcv.requestedOffload - (currentRcv.currentFuel - (currentRcv.fuelCapacity * 0.6)))
      scheduled += Math.max(0, remaining)
    }
  }
  const extra = Math.max(0, tnk.totalOffload - delivered - scheduled)
  return { delivered, scheduled, extra }
}

export function TankerDashboard({ tankers, receivers, currentTime }: TankerDashboardProps) {
  return (
    <div className="flex flex-col gap-3">
      {/* Tanker Cards */}
      <div className="grid grid-cols-1 gap-2">
        {tankers.map(tnk => {
          const { delivered, scheduled, extra } = computeOffloadBreakdown(tnk, receivers)
          const remaining = tnk.totalOffload - tnk.offloaded
          const currentRcv = tnk.currentReceiver ? receivers.find(r => r.id === tnk.currentReceiver) : null
          const total = tnk.totalOffload || 1
          const deliveredPct = (delivered / total) * 100
          const scheduledPct = (scheduled / total) * 100
          const extraPct = (extra / total) * 100

          return (
            <div key={tnk.id} className={`term-box p-3 ${tnk.status === 'CANCELLED' ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-mono font-bold text-term-primary">{tnk.callsign}</span>
                  <span className="text-[10px] text-term-textDim ml-2">{tnk.assetType}</span>
                  <span className="text-[10px] text-term-textDim ml-2">{tnk.arTrack}</span>
                </div>
                <StatusBadge status={tnk.status} />
              </div>

              {/* Stacked offload bar: delivered (green) | scheduled (blue) | extra (gray) */}
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[9px] text-term-textDim w-14 shrink-0">OFFLOAD</div>
                <div className="flex-1 h-2 bg-term-bgDark rounded-full overflow-hidden flex">
                  <div
                    className="h-2 bg-emerald-500 transition-all duration-300"
                    style={{ width: `${deliveredPct}%` }}
                    title={`Delivered: ${Math.round(delivered / 1000)}K`}
                  />
                  <div
                    className="h-2 bg-blue-500/60 transition-all duration-300"
                    style={{ width: `${scheduledPct}%` }}
                    title={`Scheduled: ${Math.round(scheduled / 1000)}K`}
                  />
                  <div
                    className="h-2 bg-gray-600/40 transition-all duration-300"
                    style={{ width: `${extraPct}%` }}
                    title={`Extra: ${Math.round(extra / 1000)}K`}
                  />
                </div>
                <div className="text-[10px] text-term-text tabular-nums w-28 text-right">
                  {Math.round(tnk.offloaded / 1000)}K / {Math.round(tnk.totalOffload / 1000)}K lbs
                </div>
              </div>

              <div className="text-[9px] text-term-textDim mb-2 flex gap-3">
                <span>{Math.round(remaining / 1000)}K remaining</span>
                <span className="text-emerald-400">{Math.round(delivered / 1000)}K delivered</span>
                <span className="text-blue-400">{Math.round(scheduled / 1000)}K scheduled</span>
                <span className="text-gray-400">{Math.round(extra / 1000)}K extra</span>
              </div>

              {/* Current receiver */}
              {currentRcv && (
                <div className="bg-term-bgLight/50 rounded p-2 mb-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold text-cyan-400">{currentRcv.callsign}</span>
                    <StatusBadge status="ON_BOOM" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-[9px] text-term-textDim w-8">FUEL</div>
                    <FuelBar pct={fuelPct(currentRcv)} />
                    <div className="text-[10px] tabular-nums text-term-text w-12 text-right">{Math.round(fuelPct(currentRcv))}%</div>
                  </div>
                </div>
              )}

              {/* Queued receivers */}
              {tnk.queuedReceivers.length > 0 && (
                <div className="space-y-0.5">
                  {tnk.queuedReceivers.map((rcvId, idx) => {
                    const rcv = receivers.find(r => r.id === rcvId)
                    if (!rcv) return null
                    return (
                      <div key={rcvId} className="flex items-center gap-2 text-[10px]">
                        <span className="text-term-textDim w-4">{idx + 1}.</span>
                        <span className="font-mono text-term-text">{rcv.callsign}</span>
                        <span className="text-term-textDim">({rcv.assetType})</span>
                        <span className="ml-auto tabular-nums text-term-textDim">{Math.round(fuelPct(rcv))}% fuel</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Receiver Summary Table */}
      <div className="term-box p-3">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Receiver Status</div>
        <table className="term-table">
          <thead>
            <tr>
              <th>Callsign</th>
              <th>Flight</th>
              <th>Fuel</th>
              <th>Offload Req</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Tanker</th>
            </tr>
          </thead>
          <tbody>
            {receivers.map(rcv => {
              const pct = fuelPct(rcv)
              const assignedTanker = tankers.find(t =>
                t.currentReceiver === rcv.id || t.queuedReceivers.includes(rcv.id)
              )
              return (
                <tr key={rcv.id}>
                  <td className="font-mono text-term-primary text-xs">{rcv.callsign}</td>
                  <td className="text-[10px] tabular-nums text-center">×{rcv.flightSize}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <FuelBar pct={pct} />
                      <span className="text-[10px] tabular-nums w-10 text-right" style={{ color: fuelColor(pct) }}>{Math.round(pct)}%</span>
                    </div>
                  </td>
                  <td className="text-[10px] tabular-nums">{Math.round(rcv.requestedOffload / 1000)}K</td>
                  <td><StatusBadge status={rcv.status} /></td>
                  <td>
                    <span className={`text-[10px] font-bold ${
                      rcv.priority === 'HIGH' ? 'text-red-400' :
                      rcv.priority === 'MEDIUM' ? 'text-amber-400' :
                      'text-gray-400'
                    }`}>
                      {rcv.priority}
                    </span>
                  </td>
                  <td className="text-[10px] font-mono text-term-textDim">
                    {assignedTanker?.callsign ?? '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
